// <copyright file="quote-row-actions.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

import { QuoteLockState, ViewQuoteSummary } from '../../../api/dealer-api-interface-all.js';
import { QuoteRowCapabilities } from './quote-row-capabilities.js';
import { QuoteState } from '../../../api/dealer-api-interface-quote.js';
import { containsFlag } from '../../../../webmodule-common/other/ui/string-helper-functions.js';

/**
 * The actions a quote row can offer. These strings are the SAME values the ellipsis menu items used,
 * so the existing dispatch (locked runner, pre-lock gate, supplier-online gate) is untouched — only
 * the presentation changes from a dropdown to a dialog. The three copy modes replace the single
 * `copy` item, which previously chose a mode on the user's behalf.
 */
export type QuoteRowAction =
  | 'copyAlternative'
  | 'copyNewProjectQuote'
  | 'copyOutOfProject'
  | 'cancel'
  | 'accept'
  | 'approve'
  | 'reject'
  | 'delete'
  | 'resetToActive';

/**
 * Why an action is unavailable. A CODE, not a message, so this module stays free of localisation and
 * can be unit-tested on behaviour alone; the dialog maps codes to text.
 */
export type QuoteActionBlockReason =
  | 'copying'
  | 'deleting'
  | 'lockedByOther'
  | 'lockedBySelf'
  | 'projectInactive'
  | 'setWillNotTakeAnother'
  | 'notAllowedInThisState';

/**
 * Whether a block will clear on its own. The user acts on this differently — `temporary` means wait a
 * moment and retry, `structural` means it will never be possible for this quote here — so the dialog
 * can present them differently instead of showing one undifferentiated wall of grey.
 */
export type QuoteActionBlockKind = 'temporary' | 'structural';

export interface QuoteActionCard {
  action: QuoteRowAction;
  enabled: boolean;
  /** Only set when `enabled` is false. */
  reason?: QuoteActionBlockReason;
  /** Only set when `enabled` is false. */
  blockKind?: QuoteActionBlockKind;
  /**
   * Wear another action's title and legend. Still a CODE rather than a string, so the dialog keeps sole
   * ownership of the wording — this only says WHICH wording applies.
   *
   * The one user of it is `approve` presented as `accept`: when supplier review is off, or the supplier
   * has already approved, Approved IS the user's accept and the full quote screen has always captioned
   * that button "Accept" (quote-view.ts getStandardButtons). Calling it "Approve" on the row would name
   * a workflow step this configuration does not have.
   */
  presentAs?: QuoteRowAction;
}

export interface QuoteRowActionOptions {
  /** Row capabilities for this quote, already computed from data on the page. */
  capabilities: QuoteRowCapabilities;
  /** Current user, to tell "in use by someone else" from "you have it open elsewhere". */
  currentUserId: string;
  /**
   * The same resolved gate the capability model was given. Needed HERE only to caption the approve
   * card, never to enable or disable anything — the engine settles legality. Defaults to true, which
   * is the caption the review workflow uses and matches the model's own default for an unloaded config.
   */
  allowSupplierReview?: boolean;
  /** False when the owning project is cancelled/completed. The list view (cross-project) omits it. */
  projectActive?: boolean;
  /** Cyncly staff see the staff-only affordances (reset-to-active, delete while copying). */
  isStaff?: boolean;
  /** True only on the Issued page fragment, which is where reset-to-active is offered. */
  onIssuedFragment?: boolean;
  /**
   * Which copy modes the HOST can actually carry out. The quote list is cross-project and has a
   * single copy event that produces a standalone quote, so it offers `outOfProject` only; the project
   * resource view can attach the copy to its project and offers all three. Stated explicitly here,
   * rather than left to each view to filter, so the two can never quietly disagree about what a row
   * can do — and so a card is never shown that its host has no code to execute.
   */
  supportedCopyModes?: readonly QuoteRowAction[];
  /**
   * Whether the HOST can carry out a delete. Same contract as `supportedCopyModes` and for the same
   * reason: a card must never be shown that its host has no code to execute. The quote list implements
   * delete; the project resource view does not (its dispatch handles copies and state changes only,
   * and its old ellipsis never offered delete), so a Delete card there was dead — it opened, looked
   * legitimate on any Draft, and did nothing at all when clicked. Defaults to true so the list view
   * and any future host keep the existing behaviour by saying nothing.
   */
  supportsDelete?: boolean;
}

/**
 * Decide which actions a row shows, and for each whether it is enabled — and if not, why.
 *
 * <b>Hide vs disable is the rule that keeps this dialog useful.</b> An action is HIDDEN when it is not
 * part of this quote's lifecycle right now (Accept on a Draft is not "blocked", it is simply not a
 * thing yet). It is SHOWN BUT DISABLED when it would normally be available here and is blocked at this
 * moment — a lock, a cancelled project, a full quote set. Disabling everything conceivable would bury
 * the two or three actions that actually apply; hiding the transiently-blocked ones would silently
 * remove the affordance the user is looking for and leave them with no explanation, which is the very
 * defect this dialog exists to fix.
 *
 * Visibility below reproduces exactly what the ellipsis menus did, so this is a presentation change
 * and not a behavioural one.
 */
export function buildQuoteRowActions(summary: ViewQuoteSummary, options: QuoteRowActionOptions): QuoteActionCard[] {
  const { capabilities, currentUserId } = options;
  const cards: QuoteActionCard[] = [];
  const isCopying = containsFlag(summary.state, QuoteState.Copying);
  const projectActive = options.projectActive ?? true;

  // The single reason that applies to every action while an operation holds the quote. Temporary by
  // definition — a copy or delete finishes (or its TTL reclaims the lock).
  const operationReason: QuoteActionBlockReason | undefined =
    capabilities.lockState === QuoteLockState.Copying
      ? 'copying'
      : capabilities.lockState === QuoteLockState.Deleting
        ? 'deleting'
        : undefined;

  const add = (action: QuoteRowAction, enabled: boolean, reason?: QuoteActionBlockReason, presentAs?: QuoteRowAction) =>
    cards.push(
      enabled
        ? { action, enabled: true, presentAs }
        : {
            action,
            enabled: false,
            reason: reason ?? 'notAllowedInThisState',
            blockKind: blockKindOf(reason ?? 'notAllowedInThisState'),
            presentAs
          }
    );

  // ---- copy: one item becomes three explicit modes -------------------------------------------
  // Hidden entirely while the quote itself is a copy destination mid-copy (its rows are still being
  // written); that matched the old menu, which dropped the copy item on the Copying flag.
  const supportedCopyModes = options.supportedCopyModes ?? [
    'copyAlternative',
    'copyNewProjectQuote',
    'copyOutOfProject'
  ];
  const addCopy = (action: QuoteRowAction, allowed: boolean, mode: CopyMode) => {
    if (!supportedCopyModes.includes(action)) return;
    add(action, allowed, copyBlockReason(mode));
  };

  if (!isCopying) {
    addCopy('copyAlternative', capabilities.copy.alternative, 'alternative');
    addCopy('copyNewProjectQuote', capabilities.copy.newProjectQuote, 'newProjectQuote');
    addCopy('copyOutOfProject', capabilities.copy.outOfProject, 'outOfProject');
  }

  // ---- state actions: visible only where they are part of the lifecycle ------------------------
  //
  // ISSUE IS DELIBERATELY ABSENT, and this is a decision rather than an omission (2026-07-31). Two
  // reasons, either of which is sufficient:
  //  1. It cannot be gated here. `canIssueQuote()` needs the loaded quote container AND its project —
  //     deferred-pricing flag, valid-until date, critical annotations, the project's street address
  //     and post code, the quote's shipping address, its title. None of that is on ViewQuoteSummary,
  //     so offering Issue would either mean loading a quote and a project per row (the exact cost this
  //     model exists to avoid) or showing a card that cannot say whether it will work.
  //  2. Issuing should not be a one-click from a row. It generates documents and sends mail; the user
  //     is better served reviewing the whole quote first, which is where the issue flow already lives.
  // Adding it later is a scope decision for the owning PBI, not a gap to be quietly filled in.
  if (summary.state === QuoteState.Active) {
    add('cancel', capabilities.canCancel, operationReason);
  }
  if (summary.state === QuoteState.Issued) {
    addAcceptOrApprove();
    add('reject', capabilities.canReject, operationReason);
  }
  if (options.isStaff && options.onIssuedFragment && summary.state === QuoteState.IssuePending) {
    add('resetToActive', !operationReason, operationReason);
  }

  // ---- delete ---------------------------------------------------------------------------------
  if ((options.supportsDelete ?? true) && (summary.state === QuoteState.Draft || (isCopying && options.isStaff))) {
    add('delete', capabilities.canDelete, operationReason);
  }

  return cards;

  /**
   * ONE card for the accept gesture, chosen by the state the quote is actually in.
   *
   * Accept and Approve are two routes through the same decision, and which one is legal is settled by
   * the engine from the quote's state, its siblings, the supplier's approval and the supplier-review
   * setting. Three of the four configurations of an Issued quote make exactly one of them legal. The
   * fourth — review off, supplier not yet approved — permits both, and Approve takes it, because that
   * is the route with review off: Accepted is a review-workflow state, and entering it here would park
   * a supplier quote where no dealer transition leads onward.
   *
   * Presenting them as one card is not merely tidier than two. Offering both would ask the user to
   * choose between a step and the step after it, and the disabled-card treatment cannot help: whichever
   * is illegal would sit there greyed and unexplainable, since the reason is not a block but simply
   * that this workflow does not have that step.
   */
  function addAcceptOrApprove(): void {
    if (capabilities.canApprove) {
      // Approve wears the Accept caption unless supplier review genuinely lies ahead of it, matching
      // quote-view.ts. From Issued that is always so; the condition is written out in full anyway,
      // because reducing it to `true` would silently mis-caption the day a card is added elsewhere.
      const reviewStillToCome = (options.allowSupplierReview ?? true) && !summary.supplierApproved;
      add('approve', true, operationReason, reviewStillToCome ? undefined : 'accept');
      return;
    }
    add('accept', capabilities.canAccept, operationReason);
  }

  function copyBlockReason(mode: CopyMode): QuoteActionBlockReason | undefined {
    // A held lock outranks the structural reasons: it is why the server would refuse, and unlike a
    // cancelled project it is something the user can actually resolve. Note `lockedBySelf` is a real
    // dead end rather than a nicety — the server's copy acquire is a plain compare-and-set that
    // refuses ANY held lock including the caller's own, so a user with the quote open in a second tab
    // is blocked by themselves and has no way to deduce that from a greyed control.
    if (operationReason) return operationReason;
    if (capabilities.lockState === QuoteLockState.Locked) {
      return summary.lockOwnerId === currentUserId ? 'lockedBySelf' : 'lockedByOther';
    }
    if (mode !== 'outOfProject' && !projectActive) return 'projectInactive';
    if (mode === 'alternative') return 'setWillNotTakeAnother';
    return undefined;
  }
}

type CopyMode = 'alternative' | 'newProjectQuote' | 'outOfProject';

/** Temporary blocks clear by themselves; structural ones need the user to change something. */
export function blockKindOf(reason: QuoteActionBlockReason): QuoteActionBlockKind {
  switch (reason) {
    case 'copying':
    case 'deleting':
    case 'lockedByOther':
    case 'lockedBySelf':
      return 'temporary';
    default:
      return 'structural';
  }
}
